<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { sortDropdownOptions } from '@/utils/globals'

export interface Option {
  id: number
  label: string
  value?: string
  pictogramUrl?: string
}

const props = defineProps<{
  modelValue: number[]
  options: Option[]
  placeholder?: string
  class?: string
  id?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: number[]]
}>()

const isOpen = ref(false)
const searchQuery = ref('')
const containerRef = ref<HTMLElement>()

// Filter options based on search query
const sortedOptions = computed(() => sortDropdownOptions(props.options))

const filteredOptions = computed(() => {
  if (!searchQuery.value) return sortedOptions.value
  const query = searchQuery.value.toLowerCase()
  return sortedOptions.value.filter(option =>
    option.label.toLowerCase().includes(query) ||
    option.value?.toLowerCase().includes(query)
  )
})

// Check if option is selected
const isSelected = (optionId: number) => {
  return props.modelValue.includes(optionId)
}

// Toggle option selection
const toggleOption = (optionId: number) => {
  const newValue = [...props.modelValue]
  const index = newValue.indexOf(optionId)
  
  if (index > -1) {
    newValue.splice(index, 1)
  } else {
    newValue.push(optionId)
  }
  
  emit('update:modelValue', newValue)
  // Keep dropdown open - don't close it
}

// Close dropdown manually
const closeDropdown = () => {
  isOpen.value = false
  searchQuery.value = ''
}


// Get selected labels for display
const selectedLabels = computed(() => {
  if (props.modelValue.length === 0) return ''
  const selected = sortedOptions.value
    .filter(opt => props.modelValue.includes(opt.id))
    .map(opt => opt.value || opt.label)
  return selected.length > 2 
    ? `${selected.slice(0, 2).join(', ')} +${selected.length - 2} weitere`
    : selected.join(', ')
})
</script>

<template>
  <div ref="containerRef" class="relative w-full">
    <DropdownMenu :open="isOpen" @update:open="isOpen = $event">
      <DropdownMenuTrigger asChild>
        <Button
          :id="id"
          variant="outline"
          :class="cn('w-full justify-between', props.class)"
        >
          <span class="truncate">
            {{ selectedLabels || placeholder || 'Auswählen...' }}
          </span>
          <svg
            class="ml-2 h-4 w-4 shrink-0 opacity-50"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent 
        class="w-[var(--radix-dropdown-menu-trigger-width)] max-h-64 overflow-auto"
        @select.prevent
        @click.stop
      >
        <DropdownMenuLabel>
          <Input
            v-model="searchQuery"
            placeholder="Suchen..."
            class="h-8"
            @click.stop
            @keydown.stop
            @mousedown.stop
          />
        </DropdownMenuLabel>

        <DropdownMenuGroup>
          <DropdownMenuItem
            v-for="option in filteredOptions"
            :key="option.id"
            class="flex items-center gap-2 cursor-pointer"
            @select.prevent
            @click.stop="toggleOption(option.id)"
            @mousedown.stop
          >
            <input
              type="checkbox"
              :checked="isSelected(option.id)"
              class="accent-blue-500 pointer-events-none"
              @change.prevent
            />
            <slot name="option" :option="option">
              <div class="flex flex-col">
                <span class="font-medium">{{ option.value || option.label }}</span>
                <span v-if="option.value && option.label !== option.value" class="text-xs text-muted-foreground">{{ option.label }}</span>
              </div>
            </slot>
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <div
          v-if="filteredOptions.length === 0"
          class="px-2 py-4 text-sm text-muted-foreground text-center"
        >
          Keine Ergebnisse gefunden
        </div>

        <div class="border-t p-2 flex justify-end">
          <Button
            type="button"
            size="sm"
            @click.stop="closeDropdown"
            @mousedown.stop
          >
            Speichern
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
</template>
